/**
 * #1988 — unpinning a lead-fixed pin, through the writes that already exist.
 *
 *   - **Unpin** (a *Fix and re-solve* pin): `DELETE /api/cycles/{id}/
 *     fixed-shifts/{pin}`. The pin goes; the seat stays as placed and the
 *     next solve may change it. Audited (`fixed_shift.deleted`, with the
 *     pin's `source_key`).
 *   - **Release this seat** (a seat opened to another grade): `POST
 *     /api/cycles/{id}/fixed-shifts/{pin}/release-seat` (#2001). The server
 *     takes ONE seat — the AM or the PM half the lead clicked, never the
 *     person's whole day; a seat on a station that books the whole day is
 *     one seat and its two halves go together. Each half's row is cleared
 *     the way the grid's Delete clears it and its pin released; the seat is
 *     empty again and the day's ladder shows it short. A pin whose row is
 *     already gone is released alone.
 *
 * No parallel path: lead-only, 404 for anyone else, both server-side.
 * Plain `apiFetch` so the callers (the day editor, the review screen) need
 * no query provider; `invalidateAfterUnpin` refreshes the reads that show
 * the consequence when one is there.
 */
import type { QueryClient } from "@tanstack/react-query";

import { type ReviewPin, splitRole } from "../lib/changeReview";
import { apiFetch } from "./client";
import type { components } from "./schema.gen";
import { cycleRosterQueryFilter } from "./useCycleRoster";
import { CYCLE_FIXED_SHIFTS_KEY } from "./useFixedShifts";
import { shortfallOptionsQueryFilter } from "./useShortfallOptions";

type ReleaseSeatResponse = components["schemas"]["ReleaseSeatResponse"];

/** A lead-fixed pin as the roster read carries it. */
export interface UnpinTarget {
  id: string;
  member_id: string;
  role: string;
  waives_eligibility?: boolean | null | undefined;
}

/**
 * Unpin / release ONE lead-fixed seat's `pins` in window `cycleId` — the
 * day editor's entry for that seat (one pin, or a full-day seat's AM + PM).
 * An acting-up seat is released once (the server takes the full-day
 * seat's other half with it); a re-solve pin is unpinned pin by pin.
 */
export async function releaseLeadFixedPins(
  cycleId: string,
  pins: readonly UnpinTarget[],
): Promise<void> {
  const done = new Set<string>();
  for (const pin of pins) {
    if (done.has(pin.id)) continue;
    done.add(pin.id);
    if (pin.waives_eligibility) {
      const res = await apiFetch<ReleaseSeatResponse>(
        `/api/cycles/${cycleId}/fixed-shifts/${pin.id}/release-seat`,
        { method: "POST" },
      );
      for (const half of res?.released ?? []) done.add(half.fixed_shift_id);
      continue;
    }
    await apiFetch(`/api/cycles/${cycleId}/fixed-shifts/${pin.id}`, { method: "DELETE" });
  }
}

/** Refresh the roster, the day's ladder and the Fixed shifts list. */
export function invalidateAfterUnpin(qc: QueryClient | undefined, cycleIds: readonly string[]) {
  if (!qc) return;
  // Every window's roster in one call: the shared reads refetch once.
  void qc.invalidateQueries(cycleRosterQueryFilter(new Set(cycleIds)));
  for (const cycleId of new Set(cycleIds)) {
    void qc.invalidateQueries(shortfallOptionsQueryFilter(cycleId));
    void qc.invalidateQueries({ queryKey: CYCLE_FIXED_SHIFTS_KEY(cycleId) });
  }
}

// ─── The review screen: pins unpinned before they were written ─────────

/** Where a review's pins land: the team and the window the flow opened on. */
export interface ReviewUnpinScope {
  teamId: string;
  cycleId: string;
}

interface FixedShiftRow {
  id: string;
  membership_id: string;
  date: string;
  role: string;
  status: string;
}

interface WindowRow {
  id: string;
  horizon_start: string;
  horizon_days: number;
}

/** Does a persisted role hold this review pin (station, optional session)? */
export function pinHoldsRole(pin: ReviewPin, role: string): boolean {
  if (!pin.station) return false;
  const held = splitRole(role);
  if (held.station !== pin.station) return false;
  if (!pin.session || pin.session === "FULL_DAY") return true;
  return held.session === pin.session;
}

function windowHolds(w: WindowRow, iso: string): boolean {
  const start = new Date(`${w.horizon_start}T00:00:00Z`).getTime();
  const day = new Date(`${iso}T00:00:00Z`).getTime();
  return day >= start && day < start + w.horizon_days * 86_400_000;
}

/**
 * After a reviewed apply wrote them, release the pins the lead unpinned on
 * the review screen: the seat lands as placed, but is not kept pinned (the
 * same end state as **Unpin** on the cell). Each pin is looked up in the
 * window the flow opened on, then in the team's window owning its date.
 * Returns the windows it touched.
 */
export async function releaseReviewPins(
  scope: ReviewUnpinScope,
  pins: readonly ReviewPin[],
): Promise<string[]> {
  const lists = new Map<string, FixedShiftRow[]>();
  const listOf = async (cycleId: string): Promise<FixedShiftRow[]> => {
    const cached = lists.get(cycleId);
    if (cached) return cached;
    const res = await apiFetch<{ fixed_shifts: FixedShiftRow[] }>(
      `/api/cycles/${cycleId}/fixed-shifts`,
    );
    lists.set(cycleId, res.fixed_shifts);
    return res.fixed_shifts;
  };
  let windows: WindowRow[] | null = null;
  const touched = new Set<string>();
  for (const pin of pins) {
    if (pin.off || !pin.station) continue;
    const matches = (rows: FixedShiftRow[]) =>
      rows.filter(
        (f) =>
          f.status === "approved" &&
          f.membership_id === pin.membership_id &&
          f.date === pin.date &&
          pinHoldsRole(pin, f.role),
      );
    let cycleId = scope.cycleId;
    let found = matches(await listOf(cycleId));
    if (found.length === 0) {
      windows ??= await apiFetch<WindowRow[]>(`/api/teams/${scope.teamId}/cycles`);
      const owner = windows.find((w) => w.id !== scope.cycleId && windowHolds(w, pin.date));
      if (owner) {
        cycleId = owner.id;
        found = matches(await listOf(cycleId));
      }
    }
    for (const f of found) {
      await apiFetch(`/api/cycles/${cycleId}/fixed-shifts/${f.id}`, { method: "DELETE" });
      touched.add(cycleId);
    }
  }
  return [...touched];
}
